package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.element.ItemStack
import kotlin.jvm.JvmStatic

/**
 * 静态 DSL 物品（element/ItemStack）的 customNbt → SNBT 转换工具。
 *
 * 仅携带 customNbt（足以覆盖 allincore.item.item_id 与 nbt 条件规则，
 * 让客户端解析出自定义 bbmodel/贴图）；displayName/lore 的 JSON Component 是 MC 专有，
 * 平台无关层不在此处理。
 *
 * SNBT 序列化/反序列化依赖 `nbt-types`（纯 JVM 库），故经 [staticItemSnbt]/[staticItemMapOf]
 * expect/actual 收口：JVM actual 走 SnbtCodec；JS 端物品渲染为占位语义（ADR-0068 §6），actual 返回 null。
 */
object StaticItemNbt {

    /**
     * 把静态 ItemStack 的 customNbt 序列化为 SNBT 字符串；空则返回 null。
     */
    @JvmStatic
    fun snbtOf(item: ItemStack?): String? {
        if (item == null || item.customNbtSnapshot().isEmpty()) {
            return null
        }
        return staticItemSnbt(item)
    }

    /**
     * 带元素级缓存的 SNBT 序列化：优先返回 [ItemStack.cachedSnbt]，未命中时回退到 [snbtOf] 并回填缓存。
     *
     * 渲染热路径每帧调用时避免重复序列化；customNbt 变化后缓存自动失效。
     */
    @JvmStatic
    fun snbtOfCached(item: ItemStack?): String? {
        if (item == null) return null
        item.cachedSnbt()?.let { return it }
        return item.cacheSnbt(snbtOf(item))
    }

    /**
     * 把 SNBT 字符串反解析为 customNbt Map（[snbtOf] 的逆向）；
     * 空串 / 解析失败 / 非 Compound / 空 Compound 均返回 null。
     *
     * 供数据驱动 slot（`item = map`）从服务端下发的 `nbtSnbt` 还原物品完整 NBT，
     * 使 texture-rules / model-rules 命中自定义 bbmodel/贴图；产出与 `ItemView.nbtAll()`
     * 语义一致（同为物品 NBT 根的 Map 形态）。
     */
    @JvmStatic
    fun mapOf(snbt: String?): Map<String, Any>? = staticItemMapOf(snbt)
}

/** SNBT 序列化的平台接缝：调用方已保证 item 非空且 customNbt 非空。 */
internal expect fun staticItemSnbt(item: ItemStack): String?

/** SNBT 反序列化的平台接缝：空串 / 非法 / 空 compound 返回 null。 */
internal expect fun staticItemMapOf(snbt: String?): Map<String, Any>?
